import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { not } from 'ember-truth-helpers';
import type QuestionnaireField from 'garaje/models/questionnaire-field';
import type QuestionnaireSection from 'garaje/models/questionnaire-section';
import { SignInFieldKind } from 'garaje/utils/enums';
import type { LocaleKey } from 'garaje/utils/locale-options';
import type QuestionnaireValidator from 'garaje/utils/questionnaire-validator';
import sortByPosition from 'garaje/utils/sort-by-position';

import Question from './question';

interface VisitorsQuestionnairesFormSectionQuestionsSignature {
  Args: {
    defaultLocale: LocaleKey;
    onChange: (models: Model[]) => void;
    readOnly?: boolean;
    section: QuestionnaireSection;
    selectedLocale: LocaleKey;
    validator: QuestionnaireValidator;
  };
}

function inputId(question: QuestionnaireField): string {
  return `input-${guidFor(question)}`;
}

export default class SectionQuestions extends Component<VisitorsQuestionnairesFormSectionQuestionsSignature> {
  @service declare store: Store;

  get canDragOrDelete(): boolean {
    // Only allow questions to be reordered or removed if all of these conditions are true:
    // 1) We're not in a read-only mode, and
    // 2) There are at least two questions, and
    // 3) The user is not viewing a non-default language
    if (this.args.readOnly) return false;
    if (!this.hasMultipleQuestions) return false;
    if (this.isTranslating) return false;
    return true;
  }

  get dragGroupName(): string {
    return `section-${guidFor(this.args.section)}-questions`;
  }

  get hasMultipleQuestions(): boolean {
    return this.questions.length > 1;
  }

  get isTranslating(): boolean {
    return this.args.selectedLocale !== this.args.defaultLocale;
  }

  get questions(): QuestionnaireField[] {
    return this.args.section.questionnaireFields.filter((question) => !question.isDeleted).toSorted(sortByPosition);
  }

  get showAddQuestionButton(): boolean {
    // Show the "Add question" button if:
    // 1) We're not in a read-only mode, and
    // 2) The user is not viewing a non-default language
    if (this.args.readOnly || this.isTranslating) return false;
    return true;
  }

  @action
  addQuestion(): void {
    const highestPosition = Math.max(...this.questions.map((q) => q.position));
    const newQuestion = this.store.createRecord('questionnaire-field', {
      questionnaireSection: this.args.section,
      kind: SignInFieldKind.SINGLE_SELECTION,
      position: highestPosition + 1,
    });
    const options = [
      this.store.createRecord('questionnaire-field-option', {
        questionnaireField: newQuestion,
        value: '',
        position: 1,
      }),
      this.store.createRecord('questionnaire-field-option', {
        questionnaireField: newQuestion,
        value: '',
        position: 2,
      }),
    ];

    this.args.onChange([newQuestion, ...options]);

    const questionTextField = document.getElementById(inputId(newQuestion));
    questionTextField?.focus();
  }

  @action
  deleteQuestion(question: QuestionnaireField): void {
    question.deleteRecord();

    const changedModels = [question];

    // renumber positions
    const questions = this.questions;
    questions.forEach((q, index) => {
      q.position = index + 1;
      if (<boolean>(q.hasDirtyAttributes as unknown)) changedModels.push(q);
    });

    this.args.onChange(changedModels);
  }

  @action
  reorder(questions: QuestionnaireField[]): void {
    questions.forEach((question, index) => {
      question.position = index + 1;
    });

    this.args.onChange(questions);
  }

  <template>
    <div class="flex">
      <header class="text-lg mb-4">
        <strong>Section {{@section.position}}</strong>
        Questions
      </header>
      <p class="text-carbon-50 text-right flex-1 text-sm">
        We recommend 5 questions maximum per section
      </p>
    </div>

    <ol {{sortableGroup onChange=this.reorder disabled=(not this.canDragOrDelete) groupName=this.dragGroupName}}>
      {{#each this.questions as |question|}}
        <Question
          class="mb-4"
          @defaultLocale={{@defaultLocale}}
          @deleteQuestion={{this.deleteQuestion}}
          @dragGroupName={{this.dragGroupName}}
          @inputId={{inputId question}}
          @isDeletable={{this.canDragOrDelete}}
          @isDraggable={{this.canDragOrDelete}}
          @onChange={{@onChange}}
          @question={{question}}
          @readOnly={{@readOnly}}
          @selectedLocale={{@selectedLocale}}
          @validator={{@validator}}
        />
      {{/each}}
    </ol>

    <div>
      {{#if this.showAddQuestionButton}}
        <button type="button" class="btn btn--outline !bg-white" {{on "click" this.addQuestion}} data-test-add-question>
          {{svgJar "plus-round"}}
          Add question
        </button>
      {{/if}}
    </div>
  </template>
}
